// Módulos adicionales en 3D: GPS NEO-6M con antena, sensor táctil TTP223 y láser KY-008.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape, glow, setGlow, emissiveMat, setEmissive } from '../kit.js';
import { GPS } from '../../parts/extras.js';
import { getPins, PITCH } from '../../parts/registry.js';

const texCache = new Map();
function cached(key, make) {
  if (!texCache.has(key)) {
    const t = make();
    t.userData.shared = true;
    texCache.set(key, t);
  }
  return texCache.get(key);
}

export function buildGps(def, props) {
  const pins = getPins(def.type, props);
  const b = new Builder();
  const top = GPS.pcbY + GPS.t;
  const silk = cached('gps', () => {
    const { c, g } = canvas(GPS.w, GPS.d, 20);
    g.fillStyle = '#1d4fae';
    g.fillRect(0, 0, GPS.w, GPS.d);
    for (const p of pins) text(g, p.name, p.x + GPS.w / 2, p.z + GPS.d / 2 - 3.4, { size: 1.2, color: '#eef3ff', weight: 700, rotate: -Math.PI / 2, align: 'left' });
    text(g, 'GY-GPS6MV2', GPS.w / 2, 4, { size: 1.6, color: '#eef3ff', weight: 700 });
    return texture(c);
  });
  b.root.add(extrudeBoard(boardShape(GPS.w, GPS.d, { r: 1 }), GPS.t, silk, '#163f8f', { w: GPS.w, d: GPS.d, y0: GPS.pcbY, holes: [[-9.5, -14.5, 1.4], [9.5, -14.5, 1.4]] }));
  const shield = cached('neo6m', () => {
    const { c, g } = canvas(16, 12.2, 24);
    g.fillStyle = '#c9ccd0';
    g.fillRect(0, 0, 16, 12.2);
    text(g, 'u-blox', 8, 4, { size: 2.2, color: '#3b3f45', weight: 800 });
    text(g, 'NEO-6M-0-001', 8, 7.4, { size: 1.3, color: '#4a4e55', weight: 600 });
    return texture(c);
  });
  const mod = new THREE.Mesh(new THREE.BoxGeometry(16, 2.4, 12.2), [M.steel, M.steel, new THREE.MeshStandardMaterial({ map: shield, roughness: 0.35, metalness: 0.7 }), M.steel, M.steel, M.steel]);
  mod.position.set(0, top + 1.2, -3);
  mod.castShadow = true;
  b.root.add(mod);
  b.cyl(2.9, 2.9, 1.6, M.steel, -7, top, 8, 24);
  b.box(2.5, 0.6, 3.5, M.chip, 5, top, 8);
  // Conector y antena de parche (unida por un cable coaxial delgado)
  const zH = GPS.d / 2 + 1.2;
  b.box(4 * PITCH, 2.5, PITCH, M.blackPlastic, 0, top, zH);
  for (const p of pins) b.box(0.64, 8.6, 0.64, M.gold, p.x, top, zH);
  const ax = GPS.w / 2 + 18;
  b.box(25, 4, 25, mat('#c7b38c', { roughness: 0.6 }), ax, 0, -6);
  b.box(18, 0.4, 18, M.steel, ax, 4, -6);
  b.cyl(1.4, 1.4, 0.6, mat('#b9a27a'), ax, 4.4, -6, 16);
  const coax = new THREE.CatmullRomCurve3([new THREE.Vector3(GPS.w / 2 - 3, top + 1, -9), new THREE.Vector3(GPS.w / 2 + 2, top + 3, -13), new THREE.Vector3(ax - 8, 3, -14), new THREE.Vector3(ax - 12.5, 2, -10)]);
  b.add(new THREE.TubeGeometry(coax, 20, 0.6, 6, false), mat('#151618', { roughness: 0.6 }));
  b.build();
  const ledMat = emissiveMat('#24452a', '#36ff4a');
  const led = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.6, 0.9), ledMat);
  led.position.set(6, top + 0.3, 12);
  b.root.add(led);
  const halo = glow('#36ff4a', 8);
  halo.position.set(6, top + 1.4, 12);
  b.root.add(halo);
  let fix = false;
  let t = 0;
  return {
    root: b.root,
    update(v) {
      if (v.pulse !== undefined && v.fix) t = 0;
      fix = !!v.fix;
    },
    animate(dt) {
      // Con posición, el LED parpadea una vez por segundo.
      t += dt;
      const on = fix && t < 0.12;
      setEmissive(ledMat, on ? 1 : 0, 14);
      setGlow(halo, on ? 0.8 : 0);
      return fix || t < 0.2;
    },
    reset() {
      fix = false;
      setEmissive(ledMat, 0, 14);
      setGlow(halo, 0);
    },
  };
}

export function buildTouch(def, props) {
  const pins = getPins(def.type, props);
  const b = new Builder();
  const y0 = 2.6;
  const silk = cached('ttp223', () => {
    const { c, g } = canvas(15, 11, 30);
    g.fillStyle = '#c4262a';
    g.fillRect(0, 0, 15, 11);
    text(g, 'TTP223', 7.5, 9.6, { size: 1.2, color: '#ffeaea', weight: 700 });
    return texture(c);
  });
  b.root.add(extrudeBoard(boardShape(15, 11, { r: 0.6 }), 1.0, silk, '#9a1c1f', { w: 15, d: 11, y0, holes: [] }));
  for (const p of pins) {
    b.box(0.64, y0 + 1, 0.64, M.gold, p.x, 0, p.z - 2.2);
    b.leg([[p.x, 0.6, p.z - 2.2], [p.x, 0.6, p.z], [p.x, 0, p.z]], 0.32, M.tin);
  }
  b.box(3 * PITCH, 1.4, PITCH, M.blackPlastic, 0, 0.2, 5.3);
  b.box(2.6, 0.6, 1.6, M.chip, -4.5, y0 + 1, 2.6);
  b.build();
  const pad = new THREE.Mesh(new THREE.CylinderGeometry(4.2, 4.2, 0.12, 40), new THREE.MeshStandardMaterial({ color: '#d6a54a', roughness: 0.35, metalness: 0.8 }));
  pad.position.set(1.5, y0 + 1.06, -1.4);
  pad.userData.interactive = { kind: 'press', key: 'touched', label: 'Tocar' };
  b.root.add(pad);
  const ledMat = emissiveMat('#3a1414', '#ff2a1a');
  const led = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.5, 0.8), ledMat);
  led.position.set(-4.5, y0 + 1.25, -2);
  b.root.add(led);
  const halo = glow('#ff2a1a', 7);
  halo.position.set(-4.5, y0 + 2.2, -2);
  b.root.add(halo);
  const show = (on) => {
    setEmissive(ledMat, on ? 1 : 0, 14);
    setGlow(halo, on ? 0.8 : 0);
    pad.position.y = y0 + 1.06 - (on ? 0.05 : 0);
  };
  return {
    root: b.root,
    update(v) {
      show(!!v.touched);
    },
    press(key, down) {
      show(down);
    },
    reset() {
      show(false);
    },
  };
}

export function buildLaser(def, props) {
  const pins = getPins(def.type, props);
  const b = new Builder();
  const y0 = 2.6;
  b.root.add(extrudeBoard(boardShape(18.5, 15, { r: 0.6 }), 1.4, null, '#18191c', { w: 18.5, d: 15, y0, holes: [[-6.5, 5, 1.2]] }));
  for (const p of pins) b.leg([[p.x, y0, p.z - 1.4], [p.x, 1.2, p.z], [p.x, 0, p.z]], 0.32, M.tin);
  b.box(3 * PITCH, 1.4, PITCH, M.blackPlastic, 0, 0.2, 8);
  // Cabeza del láser (latón) apuntando hacia −Z
  b.add(new THREE.CylinderGeometry(3, 3, 14, 24).rotateX(Math.PI / 2), mat('#c9a05a', { roughness: 0.3, metalness: 0.9 }), { x: 2, y: y0 + 4.4, z: -6 });
  b.add(new THREE.CylinderGeometry(1.4, 1.4, 0.3, 16).rotateX(Math.PI / 2), mat('#2b0606', { roughness: 0.1 }), { x: 2, y: y0 + 4.4, z: -13.1 });
  b.build();
  const beamMat = new THREE.MeshBasicMaterial({ color: '#ff1a1a', transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending });
  const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.35, 260, 8, 1, true).rotateX(Math.PI / 2), beamMat);
  beam.position.set(2, y0 + 4.4, -13.2 - 130);
  beam.userData.noPick = true;
  b.root.add(beam);
  const halo = glow('#ff2020', 14);
  halo.position.set(2, y0 + 4.4, -13.6);
  b.root.add(halo);
  const set = (k) => {
    beamMat.opacity = Math.min(0.85, k * 0.85);
    beam.visible = k > 0.02;
    setGlow(halo, k);
  };
  set(0);
  return {
    root: b.root,
    update(v) {
      set(v.b || 0);
    },
    reset() {
      set(0);
    },
  };
}

export function buildBmp280(def, props) {
  const pins = getPins(def.type, props);
  const b = new Builder();
  const y0 = 2.6;
  const silk = cached('bmp280', () => {
    const { c, g } = canvas(15, 11.5, 30);
    g.fillStyle = '#6b2fa0';
    g.fillRect(0, 0, 15, 11.5);
    for (const p of pins) text(g, p.name, p.x + 7.5, 8.2, { size: 0.85, color: '#f3eafc', weight: 700, rotate: -Math.PI / 2, align: 'right' });
    text(g, 'GY-BMP280', 7.5, 2.4, { size: 1.1, color: '#f3eafc', weight: 700 });
    return texture(c);
  });
  b.root.add(extrudeBoard(boardShape(15, 11.5, { r: 0.6 }), 1.0, silk, '#4f1f7a', { w: 15, d: 11.5, y0, holes: [[-5.2, -2.6, 1.1], [5.2, -2.6, 1.1]] }));
  for (const p of pins) b.leg([[p.x, y0, p.z - 0.6], [p.x, 0, p.z]], 0.32, M.tin);
  b.box(6 * PITCH, 1.4, PITCH, M.blackPlastic, 0, 0.6, 4.5);
  b.box(2.5, 0.95, 2.0, M.steel, 0, y0 + 1, -1.2);
  b.cyl(0.25, 0.25, 0.05, M.hole, 0.6, y0 + 1.95, -1.6, 8);
  b.box(2.9, 1.1, 1.6, M.chip, 4, y0 + 1, 1.6);
  b.box(1.0, 0.45, 0.5, mat('#b8a07a'), -3.6, y0 + 1, 1.4);
  b.build();
  return { root: b.root };
}

export const MODELS = { 'gps-neo6m': buildGps, 'touch-ttp223': buildTouch, 'laser-ky008': buildLaser, bmp280: buildBmp280 };
